import React, { useState } from 'react';
import { useTheme } from '@mui/joy';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Button from 'components/core/Button';

import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';

import ObservationDossier from 'components/ObservationDossier';
import ObservationForm from 'components/ObservationForm';

import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';

const EMPTY_ERRORS = {};
const EMPTY_NOTES = [];
const EMPTY_EVIDENCE = [];

const ImportedObservations = ({ observations, erroredObservations, mapping, finalize }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(null);
    const [editedObservations, setEditedObservations] = useState([]);
    const [fixedIndexes, setFixedIndexes] = useState(new Set());
    
    const handleChangeAccordion = (panel) => {
        return (event, isExpanded) => {
            setExpanded(isExpanded ? panel : false);
        }
    }

    const handleObservationEdit = (index, updatedObservation) => {
        const newEditedObservations = [...editedObservations];
        newEditedObservations[index] = updatedObservation;
        setEditedObservations(newEditedObservations);
        setFixedIndexes(prev => new Set([...prev, index]));
        setExpanded(null);
    }

    const handleFinalizeWithEdits = () => {
        finalize(editedObservations);
    }

    return (
        <Stack spacing={2} sx={{p: 2}}>
            {observations.map(({ observation, notes = [] }, index) => (
                <Accordion 
                    key={`observation-${index}`}
                    expanded={expanded === `observation-${index}`} 
                    onChange={handleChangeAccordion(`observation-${index}`)}
                >
                    <AccordionSummary>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CheckIcon color="success" />
                            <Typography variant="h6">
                                {observation.get('name')?.get ? observation.get('name').get('name') : 'No name'}
                            </Typography>
                            {observation.get('date') && observation.get('date') instanceof Date && (
                                <Typography variant="h6" color="textSecondary">
                                    {observation.get('date').toLocaleDateString()}
                                </Typography>
                            )}
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ObservationDossier
                            key={`observation-${index}`}
                            observation={observation}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}
            
            {erroredObservations.map(({ observation, notes = [], errors }, index) => {
                const isFixed = fixedIndexes.has(index);
                return (
                    <Accordion 
                        key={`error-observation-${index}`}
                        expanded={expanded === `error-observation-${index}`} 
                        onChange={handleChangeAccordion(`error-observation-${index}`)}
                    >
                        <AccordionSummary>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {isFixed ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                                <Typography variant="h6">
                                    {observation.get('name')?.get ? observation.get('name').get('name') : 'Observation with errors'}
                                </Typography>
                                {isFixed ? (
                                    <Typography variant="body2" color="success">
                                        (Fixed)
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="error">
                                        ({Object.keys(errors).length} error{Object.keys(errors).length !== 1 ? 's' : ''})
                                    </Typography>
                                )}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ObservationForm 
                                observation={observation} 
                                errors={isFixed ? EMPTY_ERRORS : errors} 
                                notes={notes}
                                evidence={EMPTY_EVIDENCE}
                                onSubmit={(updatedObservation) => handleObservationEdit(index, updatedObservation)}
                            />
                        </AccordionDetails>
                    </Accordion>
                );
            })}
            {erroredObservations.length > 0 ? (
                <Stack direction="row" spacing={2}>
                    <Button onClick={handleFinalizeWithEdits}>
                        Import Valid Observations & Edited Observations
                    </Button>
                    <Button variant="outlined" onClick={finalize}>
                        Import Valid Observations Only
                    </Button>
                </Stack>
            ) : (
                <Button onClick={finalize}>
                    Import Observations
                </Button>
            )}
        </Stack>
    );
}

export default ImportedObservations;